/* Copyright (c) 2020-present CloudIO Inc */

import { Item } from '../../registry/Component.type';
import { pageAtom } from 'designer/atoms';
import { genId } from 'designer/state';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';

export async function analyzePage({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = page[itemId];
  if (
    item.children.filter(
      (id) =>
        page[id]?.type === 'CloudDataStore' ||
        page[id]?.type === 'DataStore' ||
        page[id]?.type === 'LocalDataStore' ||
        page[id]?.type === 'ReadOnlyDataStore' ||
        page[id]?.type === 'TransientDataStore',
    ).length > 3
  ) {
    const fix = () => {
      const latestPage = platform.getPage(appUid, pageId);
      if (!latestPage) {
        return;
      }
      const newPage = { ...latestPage };
      const pageItem = { ...newPage.root };
      const children = pageItem.children.filter(
        (id) =>
          newPage[id]?.type === 'CloudDataStore' ||
          newPage[id]?.type === 'DataStore' ||
          newPage[id]?.type === 'LocalDataStore' ||
          newPage[id]?.type === 'ReadOnlyDataStore' ||
          newPage[id]?.type === 'TransientDataStore',
      );

      const folder: Item<'Folder'> = {
        parent: 'root',
        type: 'Folder',
        itemId: genId(newPage),
        props: { name: 'Data Stores', icon: { iconName: 'database' } },
        children,
      };
      newPage[folder.itemId] = folder;

      children.forEach((id) => {
        newPage[id] = { ...newPage[id], parent: folder.itemId };
      });

      pageItem.children = [
        folder.itemId,
        ...pageItem.children.filter(
          (id) =>
            !(
              newPage[id]?.type === 'CloudDataStore' ||
              newPage[id]?.type === 'DataStore' ||
              newPage[id]?.type === 'LocalDataStore' ||
              newPage[id]?.type === 'ReadOnlyDataStore' ||
              newPage[id]?.type === 'TransientDataStore'
            ),
        ),
      ];

      newPage.root = pageItem;
      platform.setter(pageAtom, newPage, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `ds-folder-${itemId}`,
      fix,
      alert: 'Organize all the Data Store Components within a Folder',
      itemId,
      severity: 'info',
    });
  }

  if (
    item.children.filter(
      (id) =>
        page[id]?.type === 'Popup' ||
        page[id]?.type === 'Drawer' ||
        page[id]?.type === 'Comments',
    ).length > 2
  ) {
    const fix = () => {
      const latestPage = platform.getPage(appUid, pageId);
      if (!latestPage) {
        return;
      }
      const newPage = { ...latestPage };
      const pageItem = { ...newPage.root };
      const children = pageItem.children.filter(
        (id) =>
          newPage[id]?.type === 'Popup' ||
          newPage[id]?.type === 'Drawer' ||
          newPage[id]?.type === 'Comments',
      );

      const folder: Item<'Folder'> = {
        parent: 'root',
        type: 'Folder',
        itemId: genId(newPage),
        props: { name: 'Popups', icon: { iconName: 'window-restore' } },
        children,
      };
      newPage[folder.itemId] = folder;

      children.forEach((id) => {
        newPage[id] = { ...newPage[id], parent: folder.itemId };
      });

      pageItem.children = [
        folder.itemId,
        ...pageItem.children.filter(
          (id) =>
            !(
              newPage[id]?.type === 'Popup' ||
              newPage[id]?.type === 'Drawer' ||
              newPage[id]?.type === 'Comments'
            ),
        ),
      ];

      newPage.root = pageItem;
      platform.setter(pageAtom, newPage, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `popup-folder-${itemId}`,
      fix,
      alert: 'Organize all the Popup Components within a Folder',
      itemId,
      severity: 'info',
    });
  }
  return [alerts, autoFixes];
}
